import { useState } from "react";

import { IWardMeasurementType } from "../../../@types/admin/AdminMeasurementType";
import { useRemoveWardMeasurementType } from "../../../hooks/mutations/admin/measurementTypes";
import { useToast } from "../../../hooks/useToast";
import {
  errorToastMessage,
  serverErrorMessage,
} from "../../../utils/HttpError";
import {
  FilterButton,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";
import { ModalBodyText } from "./AdminWards.styles";

interface IRemoveWardMeasurementTypeModalProps {
  wardId: number;
  wardName: string;
  measurementType: IWardMeasurementType;
  /** True when this is the ward's last configured type. */
  isLastType: boolean;
  onClose: () => void;
}

/**
 * Remove-measurement-type confirmation — Retool fired the DELETE off a row
 * icon behind its generic `requireConfirmation` prompt ("Are you sure you
 * want to remove the selected measurement type?"), which named neither the
 * type nor the ward (EN-5157).
 *
 * Removing the last type is not just one row less: it clears the ward's
 * configuration, which reverts every patient in it to resolving measurement
 * types from their conditions (EN-5060 §4.3.1). That case gets its own
 * warning.
 */
export function RemoveWardMeasurementTypeModal({
  wardId,
  wardName,
  measurementType,
  isLastType,
  onClose,
}: IRemoveWardMeasurementTypeModalProps) {
  const { showToast } = useToast();
  const { removeWardMeasurementType, isRemoving } =
    useRemoveWardMeasurementType(wardId);
  /** The server's reason for refusing — see the note in the edit modal. */
  const [rejection, setRejection] = useState<string | undefined>();

  const handleRemove = async () => {
    setRejection(undefined);
    try {
      await removeWardMeasurementType(measurementType.measurementTypeCode);
      showToast("The measurement type was removed successfully.", "success");
      onClose();
    } catch (error) {
      const refused = serverErrorMessage(error);
      if (refused) {
        setRejection(refused);
        return;
      }
      showToast(
        errorToastMessage(
          error,
          "There was an error removing this measurement type."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Remove measurement type</ModalTitle>
      <ModalForm>
        {rejection && <WarningNotice>{rejection}</WarningNotice>}

        <ModalBodyText>
          Remove <strong>{measurementType.measurementTypeDisplayName}</strong> (
          {measurementType.measurementTypeCode}) from{" "}
          <strong>{wardName}</strong>?
        </ModalBodyText>

        {isLastType && (
          <WarningNotice>
            This is the ward&apos;s last measurement type. Removing it clears
            the ward&apos;s configuration entirely, and every patient in it goes
            back to taking measurement types from their conditions.
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isRemoving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isRemoving}
            onClick={handleRemove}
          >
            {isRemoving ? "Removing..." : "Remove"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
